import { ArrowLeftIcon, XIcon } from '@phosphor-icons/react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEscapeRevertGuard } from '@/lib/keyboard';
import { MODAL_POP_MOTION, MODAL_SCRIM_MOTION } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;

/**
 * The DOM node of the nearest enclosing DialogContent, so a nested
 * DropdownMenu/Popover/Select can portal INTO it instead of to
 * document.body. This is the root-cause fix for the pointerdown/focus-
 * outside cascade the guards above patch around: a portaled child is only
 * ever treated as "outside" the dialog because it renders to a DOM sibling.
 * Making it a real DOM descendant means Radix's own native outside-click
 * detection sees it as inside, and neither the custom guards above nor any
 * upstream Radix fix are needed for consumers that use this container.
 * Read via `useDialogContentContainer()`; null outside any DialogContent
 * (callers should treat that as "use the default document.body portal").
 */
const DialogContentContainerContext = React.createContext<HTMLElement | null>(null);

function useDialogContentContainer(): HTMLElement | null {
  return React.useContext(DialogContentContainerContext);
}

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // A scrim must DARKEN the page in BOTH themes, so it cannot be built
      // from `--foreground`. In dark mode `--foreground` is 96% lightness, so
      // `bg-foreground/40` renders a 40% WHITE wash that makes the backdrop
      // brighter and hazier than the dialog it is supposed to recede behind —
      // verified on /settings/integrations at 1440x900 in dark on 2026-08-08.
      // (The design-system note claiming "light + dark mode both work" is
      // wrong; `sheet.tsx` carried the same latent bug and was fixed with it.)
      // `/20` was also too weak to focus attention. A literal black scrim is
      // the one value that is correct in both themes — same choice
      // `drawer.tsx` already makes — hence the scoped lint exemption.
      // eslint-disable-next-line no-restricted-syntax
      'fixed inset-0 z-50 bg-black/40',
      MODAL_SCRIM_MOTION,
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/**
 * Radix's DismissableLayer treats a click as "outside" whenever it isn't a
 * DOM descendant of this layer's own node — true for any nested Popper-based
 * popup (Popover/DropdownMenu/Select), since those portal their content to
 * document.body rather than into this dialog's subtree. Worse, a click that
 * lands entirely outside BOTH this dialog and a nested popup fires both
 * layers' independent outside-click detection at once, cascading the dialog
 * closed along with the popup it was hosting (e.g. PostBody's "Add to
 * project" popover). A modal dialog blocks pointer events on the rest of the
 * page while open, so nothing else can legitimately have an open Popper
 * overlay except its own descendants — treat any open one as reason to
 * ignore this outside click and let the overlay's own dismiss handle itself.
 *
 * Radix's own DismissableLayer (@radix-ui/react-dismissable-layer) only
 * gates on "topmost layer" for Escape (`useEscapeKeydown` checks
 * `index === context.layers.size - 1`) — pointerdown-outside and
 * focus-outside have NO such gate, so every open layer reacts independently.
 * Confirmed by reading node_modules/@radix-ui/react-dismissable-layer directly.
 */
function isNestedPopperOrPortalOpen(target: Element | null): boolean {
  const isInsideNestedPopper = !!target?.closest(
    '[data-radix-popper-content-wrapper], [data-base-ui-portal]',
  );
  const isRadixPopperOpen = Array.from(
    document.querySelectorAll('[data-radix-popper-content-wrapper]'),
  ).some((wrapper) => wrapper.querySelector('[data-state="open"]'));
  // Base UI portals unmount when their popup closes, so presence means open.
  const isBaseUiPopupOpen = !!document.querySelector('[data-base-ui-portal]');
  return isInsideNestedPopper || isRadixPopperOpen || isBaseUiPopupOpen;
}

function usePopperAwarePointerDownOutside<
  T extends (event: CustomEvent<{ originalEvent: PointerEvent }>) => void,
>(onPointerDownOutside?: T): (event: CustomEvent<{ originalEvent: PointerEvent }>) => void {
  return React.useCallback(
    (event: CustomEvent<{ originalEvent: PointerEvent }>) => {
      const target = event.target as Element | null;
      if (isNestedPopperOrPortalOpen(target)) {
        event.preventDefault();
      }
      onPointerDownOutside?.(event);
    },
    [onPointerDownOutside],
  );
}

/**
 * Same cascade, different trigger: a nested Popper's content portals to
 * document.body — a DOM sibling of this dialog's content, not a descendant
 * — so DismissableLayer's focus-outside check ("did focus land inside my own
 * node?") sees a false "focus left the dialog" the instant focus moves into
 * (or, on close, briefly through) that portaled content, and dismisses this
 * dialog on top of whatever the nested popup was already doing. Same fix as
 * pointerdown-outside: while any Popper/Base-UI overlay is open, this isn't
 * really an outside-focus event — let the overlay's own dismiss handle it.
 */
function usePopperAwareFocusOutside<
  T extends (event: CustomEvent<{ originalEvent: FocusEvent }>) => void,
>(onFocusOutside?: T): (event: CustomEvent<{ originalEvent: FocusEvent }>) => void {
  return React.useCallback(
    (event: CustomEvent<{ originalEvent: FocusEvent }>) => {
      const target = event.target as Element | null;
      if (isNestedPopperOrPortalOpen(target)) {
        event.preventDefault();
      }
      onFocusOutside?.(event);
    },
    [onFocusOutside],
  );
}

// The mobile safe-margin gutter below is expressed as a `w-*` utility, not
// `max-w-*`, on purpose. tailwind-merge groups ALL `max-w-*` utilities
// (including arbitrary values) into one conflict class — so a call site
// passing `className="max-w-2xl"` would silently strip this base gutter
// class outright (same group, no responsive prefix to disambiguate),
// leaving the dialog capped only by the much larger `max-w-2xl`/`w-full`
// and rendering edge-to-edge at mobile widths. `width` and `max-width` are
// separate CSS properties/tailwind-merge groups, so a `w-*` gutter survives
// any `max-w-*` a call site adds, while `width: calc(100% - 2rem)` still
// naturally yields to a smaller `max-width` once the viewport is wide
// enough for `sm:max-w-*` to take over — see src/components/ui/__tests__/dialog.test.tsx.
const dialogContentVariants = cva(
  cn(
    MODAL_POP_MOTION,
    'fixed top-[50%] left-[50%] z-50 grid translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border border-border/60 bg-background p-6 shadow-elevation-3 outline-none',
    // A dialog taller than the viewport used to render with its head and foot
    // clipped and no way to reach them — the desktop branch had no cap at all
    // while the mobile branch did. Scroll inside the dialog instead.
    'max-h-[calc(100vh-4rem)] overflow-y-auto',
    // `grid` with no `grid-template-columns` gives every direct child a
    // single implicit `auto`-sized column track, and an `auto` track's
    // MINIMUM size defaults to the widest child's min-content width -- the
    // exact same trap as a flex item's default `min-width: auto`, just
    // expressed through grid track sizing instead of flex-basis. A dialog
    // whose content includes anything wide enough (a `Select` trigger row,
    // an unbreakable placeholder) grew past its own `w-[calc(100%-2rem)]`
    // gutter at narrow viewports with NO visible right-side margin, because
    // this container's `overflow-y-auto` (for tall content) never set
    // `overflow-x`, so the oversized child rendered past the card edge
    // instead of wrapping or scrolling. Found live at 390px in
    // `InviteDialog` (2026-08-23 workspace-permissions-invite-ux QA pass) --
    // confirmed by setting `min-width: 0` directly on the dialog's child and
    // watching the 399px scrollWidth collapse to the correct 356px.
    // `min-w-0` on every direct child is the primitive-level fix so no
    // individual dialog body has to remember to add it itself.
    '[&>*]:min-w-0',
  ),
  {
    // ONE ladder for every dialog in the app. Before this existed the codebase
    // carried 19 distinct effective widths (360, 384, 448, 460, 512, 520, 576,
    // 640, 672, 768, 896, 960, 1000, 1020, 1024, 1152, 90vw, 100vw, none),
    // because `default` (512) jumped straight to `lg` (672) and every call site
    // that wanted something in between reached for a raw `max-w-*` className.
    // Add a rung here rather than a one-off class at the call site.
    variants: {
      size: {
        xs: 'w-[calc(100%-2rem)] sm:max-w-sm',
        sm: 'w-[calc(100%-2rem)] sm:max-w-md',
        default: 'w-[calc(100%-2rem)] sm:max-w-lg',
        // 576px — the standard form-dialog width. Measured off the density
        // reference; `lg` (672) reads as bulky for a four-control form.
        md: 'w-[calc(100%-2rem)] sm:max-w-xl',
        lg: 'w-[calc(100%-2rem)] sm:max-w-2xl',
        xl: 'w-[calc(100%-2rem)] sm:max-w-3xl',
        wide: 'w-[calc(100%-2rem)] sm:max-w-4xl',
        full: 'w-[calc(100%-2rem)] sm:max-w-6xl',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

interface DialogContentProps
  extends
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  hideCloseButton?: boolean;
  overlayClassName?: string;
  overlayStrength?: 'default' | 'strong';
  /**
   * Place a nested dialog inside its owning DialogContent rather than
   * `document.body`. Radix then treats interactions with the child as inside
   * the parent dismissable layer, preventing the parent from closing while a
   * child dialog is being used.
   */
  portalContainer?: HTMLElement | null;
  /**
   * The ONE missing half of `overlayStrength` (S-9): `overlayStrength`
   * governs the scrim, `surface` governs the panel's own fill. `'elevated'`
   * swaps `bg-background` for `bg-popover text-popover-foreground` and the
   * border from `border-border/60` to `border-border` — nothing else.
   * Unset (`'default'`) renders exactly as every DialogContent did before
   * this prop existed. Every paid A5 dialog and confirm passes both
   * `overlayStrength="strong"` and `surface="elevated"` (`PaidAdDialogFooter`'s
   * doc comment says so); no second lever for either concept (guard (i)).
   */
  surface?: 'default' | 'elevated';
  responsive?: boolean;
  showMobileHandle?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      hideCloseButton = false,
      overlayClassName,
      overlayStrength = 'default',
      portalContainer,
      surface = 'default',
      size,
      responsive = false,
      showMobileHandle = true,
      onPointerDownOutside,
      onFocusOutside,
      onEscapeKeyDown,
      ...props
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const handlePointerDownOutside = usePopperAwarePointerDownOutside(onPointerDownOutside);
    const handleFocusOutside = usePopperAwareFocusOutside(onFocusOutside);
    const handleEscapeKeyDown = useEscapeRevertGuard(onEscapeKeyDown);
    const [container, setContainer] = React.useState<HTMLElement | null>(null);
    const composedRef = React.useCallback(
      (node: React.ElementRef<typeof DialogPrimitive.Content> | null) => {
        setContainer(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<typeof node>).current = node;
      },
      [ref],
    );

    if (responsive && isMobile) {
      return (
        <DialogPortal container={portalContainer ?? undefined}>
          <DialogOverlay
            data-overlay-strength={overlayStrength}
            className={cn(
              'data-[state=open]:duration-350 data-[state=closed]:duration-250',
              // `portalContainer` set means this DialogContent is nested
              // INSIDE another DialogContent's own DOM node (the
              // pointerdown/focus-outside fix above). That ancestor already
              // painted the ONE scrim this app allows — see DialogOverlay's
              // `bg-black/40` comment. Painting a second one here compounds:
              // this overlay's `fixed inset-0` resolves against the
              // ancestor's transformed box (its `translate-x/y[-50%]`
              // creates a new containing block for descendant `fixed`
              // elements), not the viewport, so the second scrim renders as
              // an extra-dark wash confined to the ancestor dialog's card —
              // exactly the "washed out / doubly dimmed" report. Stay
              // transparent by default when nested; still covers the
              // ancestor's content for click-blocking, just doesn't paint.
              portalContainer && 'bg-transparent',
              // Black, not `--foreground` — see DialogOverlay above. tw-merge
              // drops the base scrim when this one is present, so a
              // foreground-derived value here would silently re-introduce the
              // dark-mode white wash on exactly the high-stakes dialogs that
              // opted into a STRONGER backdrop.
              // eslint-disable-next-line no-restricted-syntax
              overlayStrength === 'strong' && 'bg-black/60',
              overlayClassName,
            )}
          />
          <DialogPrimitive.Content
            ref={composedRef}
            className={cn(
              // Capped at 90vh like the desktop branch is capped, and it scrolls like the desktop branch:
              // without overflow the rest of a tall sheet could not be reached at all on a phone.
              'fixed inset-x-0 bottom-0 z-50 flex max-h-[90vh] flex-col gap-4 overflow-y-auto rounded-t-xl border-t p-6 shadow-elevation-3 outline-none',
              surface === 'elevated'
                ? 'bg-popover text-popover-foreground border-border'
                : 'bg-background border-border/60',
              'ease-fluid data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=open]:duration-350 data-[state=closed]:duration-250',
              className,
            )}
            aria-describedby={props['aria-describedby'] || undefined}
            onPointerDownOutside={handlePointerDownOutside}
            onFocusOutside={handleFocusOutside}
            onEscapeKeyDown={handleEscapeKeyDown}
            {...props}
          >
            {showMobileHandle ? (
              <div
                className="mx-auto h-1 w-10 shrink-0 rounded-full bg-muted"
                data-mobile-dialog-handle
                aria-hidden
              />
            ) : null}
            <DialogContentContainerContext.Provider value={container}>
              {children}
            </DialogContentContainerContext.Provider>
            {!hideCloseButton && (
              <DialogPrimitive.Close asChild>
                <CircleIconButton
                  ariaLabel="Close"
                  size="sm"
                  className="absolute right-3 top-3 hover:bg-muted data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
                >
                  <XIcon className="size-4" weight="regular" />
                </CircleIconButton>
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Content>
        </DialogPortal>
      );
    }

    return (
      <DialogPortal container={portalContainer ?? undefined}>
        <DialogOverlay
          data-overlay-strength={overlayStrength}
          className={cn(
            // See the matching comment in the mobile/responsive branch above:
            // a nested DialogContent (portalContainer set) sits inside an
            // ancestor DialogContent that already painted the one scrim this
            // app allows. Stay transparent by default so a nested dialog
            // never compounds into a doubly-dimmed wash confined to the
            // ancestor's box.
            portalContainer && 'bg-transparent',
            // Black, not `--foreground` — see DialogOverlay above.
            // eslint-disable-next-line no-restricted-syntax
            overlayStrength === 'strong' && 'bg-black/60',
            overlayClassName,
          )}
        />
        <DialogPrimitive.Content
          ref={composedRef}
          className={cn(
            dialogContentVariants({ size }),
            surface === 'elevated' && 'bg-popover text-popover-foreground border-border',
            className,
          )}
          aria-describedby={props['aria-describedby'] || undefined}
          onPointerDownOutside={handlePointerDownOutside}
          onFocusOutside={handleFocusOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
          {...props}
        >
          <DialogContentContainerContext.Provider value={container}>
            {children}
          </DialogContentContainerContext.Provider>
          {!hideCloseButton && (
            <DialogPrimitive.Close asChild>
              <CircleIconButton
                ariaLabel="Close"
                size="sm"
                className="absolute right-3 top-3 hover:bg-muted data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
              >
                <XIcon className="size-4" weight="regular" />
              </CircleIconButton>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = 'DialogContent';

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <div className={cn('flex flex-col gap-1 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

/**
 * A back affordance on the SAME ROW as the dialog's title.
 *
 * Extracted 2026-08-26 from three call sites that each hand-rolled it as
 * `absolute left-3 top-3` plus a left inset on the header to clear it — and
 * each picked a different inset (`pl-8`, `pl-10 sm:pl-12`, `pl-12`). Pinned to
 * the dialog corner the button floats above a multi-line description instead of
 * preceding the title, and where the inset was smaller than the button's right
 * edge it crowded the title outright. One of those sites carried a comment
 * explaining the workaround, which is how a fork reads as a decision.
 *
 * Wrap only the title; leave `DialogDescription` a sibling so it spans the full
 * width rather than indenting under the button.
 */
const DialogTitleRow = ({
  onBack,
  backLabel = 'Back',
  backRef,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  onBack?: () => void;
  backLabel?: string;
  /** For callers that move focus to the back affordance on step change. */
  backRef?: React.RefObject<HTMLButtonElement>;
}): JSX.Element => (
  <div className={cn('flex items-center gap-2', className)} {...props}>
    {onBack ? (
      <CircleIconButton
        ariaLabel={backLabel}
        size="sm"
        className="-ml-1 shrink-0"
        ref={backRef}
        onClick={onBack}
      >
        <ArrowLeftIcon className="size-4" weight="regular" />
      </CircleIconButton>
    ) : null}
    {children}
  </div>
);
DialogTitleRow.displayName = 'DialogTitleRow';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <div
    className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

/**
 * MEASURED against the app's own page titles, 2026-08-12 — do not "restore"
 * `font-semibold` here.
 *
 *   PageHeader h1 (desktop)  14px / 500 / tracking-tight
 *   DialogTitle (was)        16px / 600 / normal tracking
 *
 * Bigger, heavier AND without the tight tracking is three compounding
 * differences, which is why a dialog title read as dramatically louder than
 * the page title behind it (founder: "literally way bolder than the
 * PageHeaderManager text and other titles"). The old value came from the
 * design-system doc itself, so the RULE was the defect, not a drifting call
 * site — `docs/design-system/DESIGN-PATTERNS.md` is corrected to match.
 *
 * A dialog title may stay one step larger than the page title (it is the
 * focal point of a modal), but it must not be HEAVIER than it.
 */
const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-base font-medium tracking-tight text-foreground', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitleRow,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Sheet,
  SheetContent,
  usePopperAwarePointerDownOutside,
  usePopperAwareFocusOutside,
  useDialogContentContainer,
  DialogContentContainerContext,
};
export type { DialogContentProps };
